import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Calendar, 
  Edit, 
  Trash2, 
  Plus, 
  Search, 
  Filter, 
  Clock, 
  User, 
  Phone, 
  Mail, 
  MapPin, 
  CheckCircle, 
  XCircle, 
  AlertCircle, 
  MoreHorizontal,
  ChevronDown,
  ChevronUp,
  CalendarDays,
  Users,
  DollarSign,
  Tag,
  Move,
  GripVertical
} from 'lucide-react';
import ReactCalendar from 'react-calendar';
import { format, isSameDay, parseISO, addHours, startOfDay } from 'date-fns';
import { tr } from 'date-fns/locale';
import {
  DndContext,
  closestCenter,
  closestCorners,
  rectIntersection,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import {
  useDroppable,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import ApiService from '../../services/api';
import { generateTimeSlots, getAdminDaySlotTimes, getTimelineStartHour } from '../../utils/teamMemberSchedule';

interface Appointment {
  id: number;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  service: string;
  date: string;
  time: string;
  status: 'pending' | 'confirmed' | 'completed' | 'cancelled';
  notes?: string;
  totalPrice?: string;
  createdAt?: string;
  updatedAt?: string;
  serviceIds?: number[] | string[];
  serviceNames?: string[];
  userId?: number | null;
  teamMemberId?: number | null;
  customDuration?: number | null;
  teamMember?: {
    name: string;
    specialization: string;
  };
}

interface Service {
  id: string;
  name: string;
  price: number | string;
  duration?: number;
  isActive?: boolean;
}

interface TeamMember {
  id: number;
  name: string;
  specialization: string;
  isActive: boolean;
  workingDays?: number[];
  sundayStartHour?: number;
}

interface AppointmentManagementProps {
  onAppointmentsChange?: () => void;
  websocket?: WebSocket | null;
}

const AppointmentManagement: React.FC<AppointmentManagementProps> = ({ onAppointmentsChange, websocket }) => {
  // E-posta validasyonu
  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Telefon validasyonu
  const isValidPhone = (phone: string) => {
    // Sadece rakamları al
    const digitsOnly = phone.replace(/\D/g, '');
    // 10 veya 11 haneli olmalı
    return digitsOnly.length === 10 || digitsOnly.length === 11;
  };
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [homeSettings, setHomeSettings] = useState<any>(null);
  const [services, setServices] = useState<Service[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState<Appointment | null>(null);
  
  // Conflict checking state
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [conflictData, setConflictData] = useState<any>(null);
  const [pendingAppointmentData, setPendingAppointmentData] = useState<any>(null);
  const [editConflictData, setEditConflictData] = useState<any>(null);
  const [createConflictData, setCreateConflictData] = useState<any>(null);
  const [showDateTimePicker, setShowDateTimePicker] = useState(false);
  const [selectedDateForTimeSlots, setSelectedDateForTimeSlots] = useState<string>('');
  const [availableTimeSlots, setAvailableTimeSlots] = useState<Array<{time: string, available: boolean, conflictInfo: {customerName: string, service: string, duration: number} | undefined}>>([]);
  
  // Not modal state
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [selectedAppointmentNotes, setSelectedAppointmentNotes] = useState<{customerName: string, notes: string, date: string, time: string} | null>(null);
  
  // New state for enhanced functionality
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dateFilter, setDateFilter] = useState<string>('all');
  const [teamMemberFilter, setTeamMemberFilter] = useState<string>('all');
  const [timelineStartHour, setTimelineStartHour] = useState(9);
  // Mobil kontrolü için hook
  const [isMobile, setIsMobile] = useState(false);

  // Mobil kontrolü
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768); // md breakpoint
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Mobil durumu değiştiğinde viewMode'u güncelle
  useEffect(() => {
    setViewMode(isMobile ? 'list' : 'calendar');
  }, [isMobile]);

  const [viewMode, setViewMode] = useState<'list' | 'calendar'>(isMobile ? 'list' : 'calendar');
  const [sortBy, setSortBy] = useState<'date' | 'customer' | 'status'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Calendar view state
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [selectedCalendarDate, setSelectedCalendarDate] = useState<Date | null>(null);
  const [showTimeSlots, setShowTimeSlots] = useState(false);
  const [timeSlots, setTimeSlots] = useState<any[]>([]);
  const [draggedAppointment, setDraggedAppointment] = useState<any>(null);
  const [isViewingDay, setIsViewingDay] = useState(false);
  // Page activity tracking kaldırıldı - artık gerekli değil
  const [lastUpdateTime, setLastUpdateTime] = useState<Date | null>(null);
  
  // Manual polling trigger - sadece CRUD işlemlerinde çağrılır
  const triggerPolling = async () => {
    console.log('=== MANUAL POLLING TRIGGERED ===');
    
    try {
      // API'den güncel veriyi al
      await loadAppointments();
      console.log('=== MANUAL POLLING COMPLETED ===');
      
      // Timeline'ı zorla yenile - timestamp güncelleyerek re-render tetikle
      console.log('=== FORCING TIMELINE REFRESH ===');
      setLastUpdateTime(new Date());
      
    } catch (error) {
      console.error('Manual polling error:', error);
    }
  };
  
  const [formData, setFormData] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    serviceIds: [] as string[],
    serviceNames: [] as string[],
    teamMemberId: '',
    date: '',
    time: '',
    notes: '',
    totalPrice: '',
    status: 'pending' as Appointment['status'],
    customDuration: '' // Özel süre alanı eklendi
  });



  // WebSocket event listener
  useEffect(() => {
    const handleAppointmentsUpdate = (event: any) => {
      console.log('Appointments updated event received:', event.detail);
      if (event.detail.appointments) {
        // Backend'den gelen Sequelize objelerini frontend formatına çevir
        const formattedAppointments = event.detail.appointments.map((appointment: any) => {
          console.log('Processing appointment for WebSocket:', {
            id: appointment.id,
            serviceIds: appointment.serviceIds,
            serviceNames: appointment.serviceNames,
            serviceIdsType: typeof appointment.serviceIds,
            serviceNamesType: typeof appointment.serviceNames
          });
          
          // serviceIds ve serviceNames'i parse et
          let serviceIdsArray = appointment.serviceIds;
          let serviceNamesArray = appointment.serviceNames;
          
          // Eğer string ise JSON parse et, array ise direkt kullan
          if (typeof appointment.serviceIds === 'string') {
            try {
              serviceIdsArray = JSON.parse(appointment.serviceIds);
            } catch (e) {
              console.error('Error parsing serviceIds JSON:', e);
              serviceIdsArray = [];
            }
          } else if (!Array.isArray(appointment.serviceIds)) {
            serviceIdsArray = [];
          }
          
          if (typeof appointment.serviceNames === 'string') {
            try {
              serviceNamesArray = JSON.parse(appointment.serviceNames);
            } catch (e) {
              console.error('Error parsing serviceNames JSON:', e);
              serviceNamesArray = [];
            }
          } else if (!Array.isArray(appointment.serviceNames)) {
            serviceNamesArray = [];
          }
          
          console.log('Parsed arrays:', {
            serviceIdsArray,
            serviceNamesArray
          });
          
          let serviceName = 'Bilinmeyen Hizmet';
          
          if (serviceNamesArray && Array.isArray(serviceNamesArray) && serviceNamesArray.length > 0) {
            serviceName = serviceNamesArray.join(', ');
          } else if (appointment.service && appointment.service.name) {
            serviceName = appointment.service.name;
          } else {
            serviceName = 'Bilinmeyen Hizmet';
          }
          
          return {
            id: appointment.id,
            customerName: appointment.customerName,
            customerEmail: appointment.customerEmail,
            customerPhone: appointment.customerPhone,
            service: serviceName,
            date: appointment.date && typeof appointment.date === 'string' ? 
              appointment.date.includes('T') ? appointment.date.split('T')[0] : appointment.date : 
              appointment.date,
            time: appointment.time,
            status: appointment.status,
            notes: appointment.notes || '',
            totalPrice: appointment.totalPrice,
            createdAt: appointment.createdAt,
            updatedAt: appointment.updatedAt,
            serviceIds: serviceIdsArray,
            serviceNames: serviceNamesArray,
            userId: appointment.userId,
            teamMember: appointment.teamMember,
            customDuration: appointment.customDuration
          };
        });
        
        console.log('Formatted appointments from WebSocket:', formattedAppointments);
        setAppointments(formattedAppointments);
        setLastUpdateTime(new Date());
      }
    };

    window.addEventListener('appointments-updated', handleAppointmentsUpdate);

    return () => {
      window.removeEventListener('appointments-updated', handleAppointmentsUpdate);
    };
  }, []);

  // Tüm randevuları yükle
  const loadAppointments = async () => {
    try {
      setLoading(true);
      console.log('=== LOAD APPOINTMENTS START ===');
      
      // Cache bypass için timestamp ile çağır
      const response = await ApiService.getAllAppointments();
      
      if (!response) {
        console.warn('API response is undefined or null');
        setAppointments([]);
        return [];
      }
      
      if (!Array.isArray(response)) {
        console.warn('API response is not an array:', response);
        // Eğer response bir obje ise ve appointments array'i içeriyorsa
        if (response.appointments && Array.isArray(response.appointments)) {
          const formattedAppointments = response.appointments.map((appointment: any) => ({
            id: appointment.id,
            customerName: appointment.customerName,
            customerEmail: appointment.customerEmail,
            customerPhone: appointment.customerPhone,
            service: appointment.service?.name || 'Bilinmeyen Hizmet',
            date: appointment.date && typeof appointment.date === 'string' ? 
              appointment.date.includes('T') ? appointment.date.split('T')[0] : appointment.date : 
              appointment.date,
            time: appointment.time,
            status: appointment.status,
            notes: appointment.notes || '',
            totalPrice: appointment.totalPrice,
            createdAt: appointment.createdAt,
            updatedAt: appointment.updatedAt,
            serviceIds: appointment.serviceIds,
            serviceNames: appointment.serviceNames,
            userId: appointment.userId
          }));

          setAppointments(formattedAppointments);
          return formattedAppointments;
        }
        setAppointments([]);
        return [];
      }
      
             // API'den gelen veriyi frontend formatına dönüştür
       const formattedAppointments = response.map((appointment: any) => {

        
        // serviceIds ve serviceNames'i parse et
        let serviceIdsArray = appointment.serviceIds;
        let serviceNamesArray = appointment.serviceNames;
        
        // Eğer string ise JSON parse et, array ise direkt kullan
        if (typeof appointment.serviceIds === 'string') {
          try {
            serviceIdsArray = JSON.parse(appointment.serviceIds);
          } catch (e) {
            console.error('Error parsing serviceIds JSON:', e);
            serviceIdsArray = [];
          }
        } else if (!Array.isArray(appointment.serviceIds)) {
          serviceIdsArray = [];
        }
        
        if (typeof appointment.serviceNames === 'string') {
          try {
            serviceNamesArray = JSON.parse(appointment.serviceNames);
          } catch (e) {
            console.error('Error parsing serviceNames JSON:', e);
            serviceNamesArray = [];
          }
        } else if (!Array.isArray(appointment.serviceNames)) {
          serviceNamesArray = [];
        }
         
         let serviceName = 'Bilinmeyen Hizmet';
         
         if (serviceNamesArray && Array.isArray(serviceNamesArray) && serviceNamesArray.length > 0) {
           serviceName = serviceNamesArray.join(', ');
         } else if (appointment.service && appointment.service.name) {
           serviceName = appointment.service.name;
         } else {
           serviceName = 'Bilinmeyen Hizmet';
         }
         
                   return {
            id: appointment.id,
            customerName: appointment.customerName,
            customerEmail: appointment.customerEmail,
            customerPhone: appointment.customerPhone,
            service: serviceName,
            date: appointment.date && typeof appointment.date === 'string' ? 
              appointment.date.includes('T') ? appointment.date.split('T')[0] : appointment.date : 
              appointment.date,
            time: appointment.time,
            status: appointment.status,
            notes: appointment.notes || '',
            totalPrice: appointment.totalPrice,
            createdAt: appointment.createdAt,
            updatedAt: appointment.updatedAt,
            serviceIds: serviceIdsArray,
            serviceNames: serviceNamesArray,
            userId: appointment.userId,
            teamMemberId: appointment.teamMemberId,
            teamMember: appointment.teamMember,
            customDuration: appointment.customDuration
          };
       });
      
      // Force new reference - React state mutation bypass
      const newAppointmentsArray = [...(formattedAppointments || [])];
      setAppointments(newAppointmentsArray);
      setLastUpdateTime(new Date());
      console.log('=== LOAD APPOINTMENTS END ===');
      return newAppointmentsArray; // Fresh data döndür
    } catch (error: any) {
      console.error('Randevular yüklenirken hata:', error);
      setError('Randevular yüklenemedi: ' + (error?.message || 'Bilinmeyen hata'));
      setAppointments([]);
      return []; // Error durumunda boş array döndür
    } finally {
      setLoading(false);
    }
  };

  // Hizmetleri yükle
  const loadServices = async () => {
    try {
      const response = await ApiService.getAdminServices();
      
      let servicesData = response;
      if (response && response.services) {
        servicesData = response.services;
      }
      
      // Tüm hizmetleri al (aktif olmayanları da dahil et)
      const allServices = Array.isArray(servicesData) ? servicesData : [];
      
      // Sadece aktif hizmetleri filtrele
      const activeServices = allServices.filter((service: Service) => {
        return service.isActive !== false; // undefined veya true olanları al
      });
      
      setServices(activeServices);
    } catch (error) {
      console.error('Hizmetler yüklenirken hata:', error);
      setServices([]); // Hata durumunda boş array set et
    }
  };

  // Team members yükle
  const loadTeamMembers = async () => {
    try {
      const response = await ApiService.getAdminTeamMembers();
      setTeamMembers(response.teamMembers || []);
    } catch (error) {
      console.error('Team members yüklenirken hata:', error);
      setTeamMembers([]);
    }
  };

  // Component mount olduğunda randevuları, hizmetleri ve team members'ları yükle
  useEffect(() => {
    loadAppointments();
    loadServices();
    loadTeamMembers();
    
    // Home settings yükle
    const loadHomeSettings = async () => {
      try {
        const response = await ApiService.getHomeSettings();
        setHomeSettings(response.settings);
      } catch (error) {
        console.error('Home settings yüklenemedi:', error);
      }
    };
    loadHomeSettings();
  }, []);

  const updateAppointmentStatus = async (id: number, status: Appointment['status']) => {
    try {
      console.log('updateAppointmentStatus called:', { id, status });
      
      // Eğer onaylama işlemi ise çakışma kontrolü yap
      if (status === 'confirmed') {
        console.log('Status is confirmed, checking for conflicts...');
        const appointment = appointments.find(apt => apt.id === id);
        console.log('Found appointment for conflict check:', appointment);
        
        if (appointment) {
          const conflictData = {
            teamMemberId: appointment.teamMemberId,
            date: appointment.date,
            time: appointment.time,
            serviceIds: appointment.serviceIds
          };
          console.log('Conflict check data:', conflictData);
          
          const conflictCheck = await checkForConflicts(conflictData, id);
          console.log('Conflict check result:', conflictCheck);
          
          if (conflictCheck.hasConflict) {
            console.log('Conflict detected, showing modal');
            setConflictData(conflictCheck);
            setPendingAppointmentData({ id, status });
            setShowConflictModal(true);
            return;
          } else {
            console.log('No conflict detected, proceeding with status update');
          }
        } else {
          console.log('Appointment not found in state for conflict check');
        }
      }
      
      await ApiService.updateAppointmentStatus(id.toString(), status);
      setAppointments(prev => 
        prev.map(apt => apt.id === id ? { ...apt, status } : apt)
      );
      
      // Timeline'ı zorla yenile
      setLastUpdateTime(new Date());
      
      if (onAppointmentsChange) {
        onAppointmentsChange();
      }
    } catch (error: any) {
      console.error('Randevu durumu güncellenirken hata:', error);
      setError('Randevu durumu güncellenemedi: ' + error.message);
    }
  };

  const deleteAppointment = async (id: number) => {
    if (!window.confirm('Bu randevuyu silmek istediğinizden emin misiniz?')) {
      return;
    }
    
    try {
      await ApiService.deleteAppointmentAdmin(id.toString());
      
      // State'den anında kaldır
      setAppointments(prevAppointments => 
        prevAppointments.filter(apt => apt.id !== id)
      );
      
      // Timeline'ı zorla yenile
      setLastUpdateTime(new Date());
      
      if (onAppointmentsChange) {
        onAppointmentsChange();
      }
    } catch (error: any) {
      console.error('Randevu silinirken hata:', error);
      setError('Randevu silinemedi: ' + error.message);
    }
  };



  // Enhanced filtering and sorting logic
  const filteredAndSortedAppointments = appointments
    .filter(appointment => {
      // Search filter
      const searchMatch = searchTerm === '' || 
        appointment.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        appointment.customerEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
        appointment.customerPhone.includes(searchTerm) ||
        appointment.service.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Status filter
      const statusMatch = statusFilter === 'all' || appointment.status === statusFilter;
      
      // Date filter
      let dateMatch = true;
      if (dateFilter === 'today') {
        dateMatch = appointment.date === new Date().toISOString().split('T')[0];
      } else if (dateFilter === 'week') {
        const appointmentDate = new Date(appointment.date);
        const today = new Date();
        const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
        dateMatch = appointmentDate >= weekAgo && appointmentDate <= today;
      } else if (dateFilter === 'month') {
        const appointmentDate = new Date(appointment.date);
        const today = new Date();
        const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
        dateMatch = appointmentDate >= monthAgo && appointmentDate <= today;
      }
      
      // Team member filter
      const teamMatch = teamMemberFilter === 'all' || 
        appointment.teamMemberId?.toString() === teamMemberFilter;
      
      return searchMatch && statusMatch && dateMatch && teamMatch;
    })
    .sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'date':
          comparison = new Date(a.date + ' ' + a.time).getTime() - new Date(b.date + ' ' + b.time).getTime();
          break;
        case 'customer':
          comparison = a.customerName.localeCompare(b.customerName);
          break;
        case 'status':
          comparison = a.status.localeCompare(b.status);
          break;
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });

  // Statistics
  const stats = {
    total: appointments.length,
    pending: appointments.filter(a => a.status === 'pending').length,
    confirmed: appointments.filter(a => a.status === 'confirmed').length,
    completed: appointments.filter(a => a.status === 'completed').length,
    cancelled: appointments.filter(a => a.status === 'cancelled').length,
    today: appointments.filter(a => a.date === new Date().toISOString().split('T')[0]).length
  };

  // Calendar helper functions - useCallback ile optimize et (stale closure bypass)
  const getAppointmentsForDate = useCallback((date: Date) => {
    const dateString = format(date, 'yyyy-MM-dd');
    
    // Direkt appointments'tan filtrele - cache bypass
    const result = appointments.filter(appointment => {
      // Tarih formatını normalize et
      let appointmentDate = appointment.date;
      if (appointmentDate && typeof appointmentDate === 'string') {
        if (appointmentDate.includes('T')) {
          appointmentDate = appointmentDate.split('T')[0];
        }
        if (appointmentDate.length === 4) {
          appointmentDate = dateString;
        }
      }
      
      const isRightDate = appointmentDate === dateString;
      const isActiveStatus = appointment.status === 'pending' || appointment.status === 'confirmed';
      

      
      return isRightDate && isActiveStatus;
    });
    

    
    return result;
  }, [appointments, lastUpdateTime]); // appointments ve lastUpdateTime dependency'si

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed': return 'bg-green-500';
      case 'pending': return 'bg-yellow-500';
      case 'completed': return 'bg-blue-500';
      case 'cancelled': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const formatTime = (time: string) => {
    // Handle both 'HH:MM:SS' and 'HH:MM' formats
    if (time && time.includes(':')) {
      return time.substring(0, 5); // HH:MM format
    }
    return time;
  };

  // Calendar tile content
  const tileContent = ({ date, view }: { date: Date; view: string }) => {
    if (view === 'month') {
      const dayAppointments = getAppointmentsForDate(date);
      if (dayAppointments.length > 0) {
        return (
          <div className="absolute bottom-1 left-1 right-1">
            <div className="flex flex-wrap gap-1">
              {dayAppointments.slice(0, 3).map((appointment, index) => (
                <div
                  key={index}
                  className={`w-2 h-2 rounded-full ${getStatusColor(appointment.status)}`}
                  title={`${appointment.customerName} - ${appointment.time}`}
                />
              ))}
              {dayAppointments.length > 3 && (
                <div className="w-2 h-2 rounded-full bg-gray-400 text-xs flex items-center justify-center">
                  +
                </div>
              )}
            </div>
          </div>
        );
      }
    }
    return null;
  };

  // Form işlemleri
  const resetForm = () => {
    setFormData({
      customerName: '',
      customerEmail: '',
      customerPhone: '',
      serviceIds: [],
      serviceNames: [],
      teamMemberId: '',
      date: '',
      time: '',
      notes: '',
      totalPrice: '',
      status: 'pending',
      customDuration: ''
    });
  };

  // Check for appointment conflicts - Tamamen frontend'de kontrol
  const checkForConflicts = async (appointmentData: any, excludeAppointmentId?: number | null) => {
    if (!appointmentData.teamMemberId || !appointmentData.date || !appointmentData.time) {
      return { hasConflict: false, conflicts: [] };
    }

    try {
      console.log('checkForConflicts - Frontend only check');
      console.log('checkForConflicts - excludeAppointmentId:', excludeAppointmentId, 'Type:', typeof excludeAppointmentId);
      console.log('checkForConflicts - appointmentData:', appointmentData);
      
      // Frontend'de detaylı çakışma kontrolü yap
      const conflicts = await getDetailedConflictInfo(appointmentData, excludeAppointmentId);
      
      const hasConflict = conflicts.length > 0;
      
      console.log('checkForConflicts - Frontend result:', {
        hasConflict,
        conflictsCount: conflicts.length,
        conflicts
      });
      
      return {
        hasConflict,
        conflicts,
        message: hasConflict ? `Bu tarih ve saatte ${conflicts.length} randevu çakışması bulunuyor.` : ''
      };
    } catch (error) {
      console.error('Error checking conflicts:', error);
      return { hasConflict: false, conflicts: [] };
    }
  };

  // Detaylı çakışma bilgisi al
  const getDetailedConflictInfo = async (appointmentData: any, excludeAppointmentId?: number | null) => {
    const conflicts = [];
    
    console.log('getDetailedConflictInfo - Debug:', {
      appointmentData,
      excludeAppointmentId,
      totalAppointments: appointments.length,
      appointmentsCurrentState: appointments.map(apt => ({
        id: apt.id,
        customerName: apt.customerName,
        date: apt.date,
        time: apt.time,
        status: apt.status,
        teamMemberId: apt.teamMemberId
      }))
    });
    
    // Randevunun süresini hesapla
    let duration = 60; // dakika cinsinden (varsayılan 1 saat)
    
    // Önce özel süre kontrolü
    if (formData.customDuration && formData.customDuration.trim() !== '') {
      duration = parseInt(formData.customDuration);
    } else if (appointmentData.serviceIds && Array.isArray(appointmentData.serviceIds)) {
      const selectedServices = services.filter(service => 
        appointmentData.serviceIds.some((id: any) => id.toString() === service.id.toString())
      );
      duration = selectedServices.reduce((total, service) => 
        total + (service.duration || 60), 0
      );
    }
    
    // Yeni randevunun zaman aralığını hesapla
    const newStartTime = new Date(`2000-01-01T${appointmentData.time}`);
    const newEndTime = new Date(newStartTime.getTime() + duration * 60000);
    
    // Aynı tarih ve ekip üyesindeki diğer aktif randevuları kontrol et (tamamlanmış/iptal edilmiş randevular çakışma oluşturmaz)
    const sameDateAppointments = appointments.filter(apt => {
      let aptDate = apt.date;
      if (aptDate && typeof aptDate === 'string') {
        if (aptDate.includes('T')) {
          aptDate = aptDate.split('T')[0];
        }
        if (aptDate.length === 4) {
          aptDate = appointmentData.date;
        }
      }
      return aptDate === appointmentData.date && 
             apt.teamMemberId?.toString() === appointmentData.teamMemberId?.toString() &&
             apt.id.toString() !== excludeAppointmentId?.toString() &&
             apt.status === 'confirmed'; // Sadece onaylı randevular
    });

    console.log('getDetailedConflictInfo - Filtered appointments:', {
      sameDateAppointments: sameDateAppointments.map(apt => ({
        id: apt.id,
        customerName: apt.customerName,
        time: apt.time,
        status: apt.status
      }))
    });
    
    // Her randevu için çakışma kontrolü yap
    for (const existingApt of sameDateAppointments) {
      // Mevcut randevunun süresini hesapla
      let existingDuration = 60;
      if (existingApt.serviceIds) {
        try {
          const serviceIds = typeof existingApt.serviceIds === 'string' ? 
            JSON.parse(existingApt.serviceIds) : existingApt.serviceIds;
          
          if (Array.isArray(serviceIds)) {
            const selectedServices = services.filter(service => 
              serviceIds.some((id: any) => id.toString() === service.id.toString())
            );
            existingDuration = selectedServices.reduce((total, service) => 
              total + (service.duration || 60), 0
            );
          }
        } catch (error) {
          existingDuration = 60;
        }
      }
      
      const existingStartTime = new Date(`2000-01-01T${formatTime(existingApt.time)}`);
      const existingEndTime = new Date(existingStartTime.getTime() + existingDuration * 60000);
      
      // Çakışma kontrolü (15 dakika tolerans)
      const overlapStart = new Date(Math.max(newStartTime.getTime(), existingStartTime.getTime()));
      const overlapEnd = new Date(Math.min(newEndTime.getTime(), existingEndTime.getTime()));
      const overlapMinutes = (overlapEnd.getTime() - overlapStart.getTime()) / 60000;
      
      if (overlapMinutes > 15) {
        console.log('Conflict detected:', {
          existingAppointment: {
            id: existingApt.id,
            customerName: existingApt.customerName,
            time: existingApt.time,
            status: existingApt.status
          },
          overlapMinutes,
          newAppointmentTime: appointmentData.time
        });
        
        conflicts.push({
          appointmentId: existingApt.id,
          customerName: existingApt.customerName,
          startTime: existingApt.time,
          endTime: existingEndTime.toTimeString().substring(0, 5),
          duration: existingDuration,
          overlapMinutes: overlapMinutes,
          service: existingApt.service || 'Bilinmeyen Hizmet'
        });
      }
    }
    
    console.log('getDetailedConflictInfo - Final result:', {
      conflictsFound: conflicts.length,
      conflicts
    });
    
    return conflicts;
  };

  // Handle conflict confirmation
  const handleConflictConfirm = async () => {
    if (!pendingAppointmentData) return;

    try {
      if (editingAppointment) {
        await ApiService.updateAppointment(editingAppointment.id.toString(), pendingAppointmentData);
      } else if (pendingAppointmentData.id) {
        // Durum güncelleme işlemi
        await ApiService.updateAppointmentStatus(pendingAppointmentData.id.toString(), pendingAppointmentData.status);
        setAppointments(prev => 
          prev.map(apt => apt.id === pendingAppointmentData.id ? { ...apt, status: pendingAppointmentData.status } : apt)
        );
      } else {
        await ApiService.createAppointment(pendingAppointmentData);
      }
      
      setShowConflictModal(false);
      setShowCreateModal(false);
      setShowEditModal(false);
      setEditingAppointment(null);
      setPendingAppointmentData(null);
      setConflictData(null);
      resetForm();
      
      // Timeline'ı zorla yenile
      setLastUpdateTime(new Date());
      
      if (onAppointmentsChange) {
        onAppointmentsChange();
      }
    } catch (error: any) {
      console.error('Error saving appointment:', error);
      setError('Randevu kaydedilemedi: ' + error.message);
    }
  };

  const handleConflictCancel = () => {
    setShowConflictModal(false);
    setPendingAppointmentData(null);
    setConflictData(null);
  };

  // Edit modal'da anlık çakışma kontrolü
  const checkEditConflict = async (date: string, time: string, teamMemberId: string) => {
    if (!date || !time || !teamMemberId || !editingAppointment) {
      setEditConflictData(null);
      return;
    }

    try {
      const appointmentData = {
        ...formData,
        date,
        time,
        teamMemberId: parseInt(teamMemberId)
      };

      const conflictCheck = await checkForConflicts(appointmentData, editingAppointment.id);
      
      if (conflictCheck.hasConflict) {
        setEditConflictData(conflictCheck);
      } else {
        setEditConflictData(null);
      }
    } catch (error) {
      console.error('Error checking edit conflict:', error);
      setEditConflictData(null);
    }
  };

  // DateTime picker için müsait saatleri yükle
  const loadAvailableTimeSlots = async (date: string, teamMemberId: string) => {
    if (!date || !teamMemberId) return;

    try {
      const member = teamMembers.find((m) => m.id.toString() === teamMemberId);
      const slotTimes = generateTimeSlots(member, date);

      const slots = slotTimes.map((time) => ({
        time,
        available: true,
        conflictInfo: undefined as {customerName: string, service: string, duration: number} | undefined
      }));

      // O günün randevularını kontrol et - sadece aktif randevular (pending/confirmed)
      const dayAppointments = appointments.filter(apt => {
        let aptDate = apt.date;
        if (aptDate && typeof aptDate === 'string') {
          if (aptDate.includes('T')) {
            aptDate = aptDate.split('T')[0];
          }
          if (aptDate.length === 4) {
            aptDate = date;
          }
        }
        return aptDate === date && 
               apt.teamMemberId?.toString() === teamMemberId &&
               apt.id.toString() !== editingAppointment?.id.toString() &&
               (apt.status === 'pending' || apt.status === 'confirmed'); // Sadece aktif randevular
      });

      console.log('loadAvailableTimeSlots - Debug:', {
        date,
        teamMemberId,
        totalAppointments: appointments.length,
        dayAppointments: dayAppointments.length,
        dayAppointmentsDetails: dayAppointments.map(apt => ({
          id: apt.id,
          customerName: apt.customerName,
          time: apt.time,
          status: apt.status,
          teamMemberId: apt.teamMemberId
        }))
      });

      // Her randevunun detaylarını kontrol et
      dayAppointments.forEach(apt => {
        // Removed debug log
      });

      // Her randevu için dolu saatleri işaretle
      for (const apt of dayAppointments) {
        // Randevunun süresini al - önce duration özelliğini kontrol et
        let duration = (apt as any).duration || 60; // dakika cinsinden
        

        
        // Eğer duration yoksa serviceIds'den hesapla
        if (!(apt as any).duration && apt.serviceIds) {
          try {
            const serviceIds = typeof apt.serviceIds === 'string' ? 
              JSON.parse(apt.serviceIds) : apt.serviceIds;
            

            
            if (Array.isArray(serviceIds)) {
              const selectedServices = services.filter(service => 
                serviceIds.some((id: any) => id.toString() === service.id.toString())
              );
              
              duration = selectedServices.reduce((total, service) => 
                total + (service.duration || 60), 0
              );
            }
          } catch (error) {
            console.error('Error calculating duration for appointment:', apt.id, error);
            duration = 60;
          }
        }
        


        const formattedStartTime = formatTime(apt.time);
        const formattedStartHour = parseInt(formattedStartTime.split(':')[0]);
        const formattedStartMinute = parseInt(formattedStartTime.split(':')[1]);
        const slotsNeeded = Math.ceil(duration / 30); // 30 dakikalık slotlar için

        // Bu randevunun kapladığı tüm slotları işaretle
        for (let i = 0; i < slotsNeeded; i++) {
          const totalMinutes = formattedStartHour * 60 + formattedStartMinute + (i * 30);
          const slotHour = Math.floor(totalMinutes / 60);
          const slotMinute = totalMinutes % 60;

          const slotTime = `${slotHour.toString().padStart(2, '0')}:${slotMinute.toString().padStart(2, '0')}`;
          const slot = slots.find(s => s.time === slotTime);
          if (slot) {
              console.log(`Marking slot ${slotTime} as unavailable for appointment ${apt.id} (${apt.customerName})`);
              slot.available = false;
              slot.conflictInfo = {
                customerName: apt.customerName,
                service: apt.service,
                duration: duration
              };
          }
        }
      }


      console.log('Final slots:', slots.map(slot => ({
        time: slot.time,
        available: slot.available,
        customerName: slot.conflictInfo?.customerName
      })));
      
      setAvailableTimeSlots(slots);
    } catch (error) {
      console.error('Error loading available time slots:', error);
    }
  };

  // DateTime picker'ı aç
  const openDateTimePicker = () => {
    setSelectedDateForTimeSlots(formData.date);
    setShowDateTimePicker(true);
    if (formData.date && formData.teamMemberId) {
      loadAvailableTimeSlots(formData.date, formData.teamMemberId);
    }
  };

  // Appointments state değiştiğinde available time slots'u güncelle
  useEffect(() => {
    if (showDateTimePicker && selectedDateForTimeSlots && formData.teamMemberId) {
      loadAvailableTimeSlots(selectedDateForTimeSlots, formData.teamMemberId);
    }
  }, [appointments, showDateTimePicker, selectedDateForTimeSlots, formData.teamMemberId]);

  // Tarih seçildiğinde
  const handleDateSelect = (date: string) => {
    setSelectedDateForTimeSlots(date);
    setFormData({...formData, date});
    if (formData.teamMemberId) {
      loadAvailableTimeSlots(date, formData.teamMemberId);
    }
  };

  // Saat seçildiğinde
  const handleTimeSelect = (time: string) => {
    setFormData({...formData, time});
    setShowDateTimePicker(false);
    
    // Seçilen saatin dolu olup olmadığını kontrol et
    const selectedSlot = availableTimeSlots.find(slot => slot.time === time);
    if (selectedSlot && !selectedSlot.available) {
      // Dolu saat seçildiğinde çakışma uyarısı göster
      setCreateConflictData({
        hasConflict: true,
        message: `Seçilen saat (${time}) dolu. ${selectedSlot.conflictInfo?.customerName} adlı müşterinin randevusu bulunmaktadır.`,
        conflicts: [{
          customerName: selectedSlot.conflictInfo?.customerName || 'Bilinmeyen',
          service: selectedSlot.conflictInfo?.service || 'Hizmet',
          time: time,
          date: formData.date
        }]
      });
    } else {
      // Boş saat seçildiğinde normal çakışma kontrolü yap
      if (formData.teamMemberId) {
        if (showEditModal) {
          checkEditConflict(formData.date, time, formData.teamMemberId);
        } else if (showCreateModal) {
          checkCreateConflict(formData.date, time, formData.teamMemberId);
        }
      }
    }
  };

  // Yeni randevu için çakışma kontrolü
  const checkCreateConflict = async (date: string, time: string, teamMemberId: string) => {
    if (!date || !time || !teamMemberId) {
      setCreateConflictData(null);
      return;
    }

    try {
      const appointmentData = {
        ...formData,
        date,
        time,
        teamMemberId: parseInt(teamMemberId)
      };

      const conflictCheck = await checkForConflicts(appointmentData);
      
      if (conflictCheck.hasConflict) {
        setCreateConflictData(conflictCheck);
      } else {
        setCreateConflictData(null);
      }
    } catch (error) {
      console.error('Error checking create conflict:', error);
      setCreateConflictData(null);
    }
  };

  const buildDayTimeSlots = (date: Date, memberFilter: string = teamMemberFilter) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const slotTimes = getAdminDaySlotTimes(dateStr, teamMembers, memberFilter);
    setTimelineStartHour(getTimelineStartHour(slotTimes));

    const slots: Array<{
      id: string;
      time: string;
      appointments: Appointment[];
      date: string;
    }> = slotTimes.map((time) => ({
      id: `${dateStr}-${time}`,
      time,
      appointments: [],
      date: dateStr
    }));

    appointments.forEach(apt => {
      // Tarih formatını normalize et
      let appointmentDate = apt.date;
      if (appointmentDate && typeof appointmentDate === 'string') {
        if (appointmentDate.includes('T')) {
          appointmentDate = appointmentDate.split('T')[0];
        }
        if (appointmentDate.length === 4) {
          appointmentDate = dateStr;
        }
      }
      
      // Sadece bu tarihteki onaylı randevuları işle (timeline'da sadece confirmed randevular gösterilir)
      if (appointmentDate === dateStr && apt.status === 'confirmed') {
        // Randevunun süresini hesapla
        let duration = 60; // dakika cinsinden (varsayılan 1 saat)
        if (apt.serviceIds) {
          try {
            const serviceIds = typeof apt.serviceIds === 'string' ? 
              JSON.parse(apt.serviceIds) : apt.serviceIds;
            
            if (Array.isArray(serviceIds)) {
              // Hizmet sürelerini topla
              const selectedServices = services.filter(service => 
                serviceIds.some((id: any) => id.toString() === service.id.toString())
              );
              duration = selectedServices.reduce((total, service) => 
                total + (service.duration || 60), 0
              );
            }
          } catch (error) {
            console.error('Error parsing serviceIds:', error);
            duration = 60; // Hata durumunda varsayılan 1 saat
          }
        }
        
        // Randevuyu kapladığı tüm slot'larda göster (30 dakikalık slotlar için)
        const slotsNeeded = Math.ceil(duration / 30); // 30 dakikalık slotlar için
        const slotStartTime = formatTime(apt.time);
        const slotStartHour = parseInt(slotStartTime.split(':')[0]);
        const slotStartMinute = parseInt(slotStartTime.split(':')[1]);
        
        for (let i = 0; i < slotsNeeded; i++) {
          const totalMinutes = slotStartHour * 60 + slotStartMinute + (i * 30);
          const currentHour = Math.floor(totalMinutes / 60);
          const currentMinute = totalMinutes % 60;
          
          if (currentHour <= 18 && !(currentHour === 18 && currentMinute === 0)) {
            const slotTime = `${currentHour.toString().padStart(2, '0')}:${currentMinute.toString().padStart(2, '0')}`;
            const slot = slots.find(s => s.time === slotTime);
            if (slot) {
              const existingAppointment = slot.appointments.find(a => a.id === apt.id);
              if (!existingAppointment) {
                // Randevuya süre bilgisini ekle
                const appointmentWithDuration = {
                  ...apt,
                  duration: duration,
                  slotsNeeded: slotsNeeded,
                  isMainSlot: i === 0 // Sadece ilk slot'ta ana randevu olarak göster
                };
                console.log('Adding appointment to slot:', slotTime, 'appointment:', apt.id, 'isMainSlot:', appointmentWithDuration.isMainSlot);
                slot.appointments.push(appointmentWithDuration);
              }
            }
          }
        }
      }
    });
    
    return slots;
  };

  // Appointments state değişimini dinle ve timeline'ı force update et
  useEffect(() => {
    console.log('=== APPOINTMENTS STATE CHANGED ===');
    console.log('Triggering timeline force update...');
    setLastUpdateTime(new Date());
    
    // Eğer timeline görünümündeyse, timeSlots'ı yeniden generate et
    if (isViewingDay && selectedCalendarDate) {
      console.log('=== REGENERATING TIME SLOTS ===');
      const slots = buildDayTimeSlots(selectedCalendarDate);
      setTimeSlots(slots);
    }
  }, [appointments, isViewingDay, selectedCalendarDate, teamMemberFilter, teamMembers]);

  // Randevuları satırlara dağıt (çakışmaları önlemek için)
  const distributeAppointmentsToRows = (slots: any[]) => {
    const appointmentRows: { [key: string]: any[] } = {};
    
    // Önce tüm randevuları topla (sadece ana slot'lardaki)
    const allAppointments: any[] = [];
    slots.forEach(slot => {
      slot.appointments.forEach((appointment: any) => {
        if ((appointment as any).isMainSlot) {
          allAppointments.push(appointment);
        }
      });
    });
    
    console.log('Total appointments found:', allAppointments.length);
    
    // Randevuları başlangıç saatine göre sırala
    allAppointments.sort((a, b) => {
      const timeA = parseInt(a.time.split(':')[0]);
      const timeB = parseInt(b.time.split(':')[0]);
      return timeA - timeB;
    });
    
    // Tüm randevuları satırlara dağıt
    allAppointments.forEach(appointment => {
      console.log('Processing appointment:', appointment.id, 'time:', appointment.time, 'slotsNeeded:', appointment.slotsNeeded);
      
      // Bu randevu için uygun satır bul
      let rowIndex = 0;
      while (true) {
        const rowKey = `row-${rowIndex}`;
        if (!appointmentRows[rowKey]) {
          appointmentRows[rowKey] = [];
        }
        
        // Bu satırda çakışan randevu var mı kontrol et
        const hasConflict = appointmentRows[rowKey].some((existingAppt: any) => {
          // Zaman çakışması kontrolü
          const existingStart = parseInt(existingAppt.time.split(':')[0]);
          const existingEnd = existingStart + (existingAppt.slotsNeeded || 1);
          const newStart = parseInt(appointment.time.split(':')[0]);
          const newEnd = newStart + (appointment.slotsNeeded || 1);
          
          console.log('Conflict check - existing:', existingStart, '-', existingEnd, 'new:', newStart, '-', newEnd);
          const conflict = (newStart < existingEnd && newEnd > existingStart);
          console.log('Conflict result:', conflict);
          return conflict;
        });
        
        if (!hasConflict) {
          console.log('Adding appointment to row:', rowKey, 'appointment:', appointment.id);
          appointmentRows[rowKey].push(appointment);
          break;
        }
        rowIndex++;
      }
    });
    
    console.log('Final appointmentRows:', appointmentRows);
    return appointmentRows;
  };

  // Handle calendar date click
  const handleCalendarDateClick = (date: Date) => {
    setSelectedCalendarDate(date);
    setIsViewingDay(true);
    setShowTimeSlots(true);
    const slots = buildDayTimeSlots(date);
    setTimeSlots(slots);
  };

  // Handle back to calendar view
  const handleBackToCalendar = () => {
    setIsViewingDay(false);
    setShowTimeSlots(false);
    setSelectedCalendarDate(null);
  };

  // Çakışma kontrolü fonksiyonu
  const checkAppointmentConflict = async (appointment: Appointment, targetDate: string, targetTime: string): Promise<boolean> => {
    try {
      // Randevunun süresini hesapla
      let duration = 60; // dakika cinsinden (varsayılan 1 saat)
      if (appointment.serviceIds) {
        try {
          const serviceIds = typeof appointment.serviceIds === 'string' ? 
            JSON.parse(appointment.serviceIds) : appointment.serviceIds;
          
          if (Array.isArray(serviceIds)) {
            const selectedServices = services.filter(service => 
              serviceIds.some((id: any) => id.toString() === service.id.toString())
            );
            duration = selectedServices.reduce((total, service) => 
              total + (service.duration || 60), 0
            );
          }
        } catch (error) {
          duration = 60;
        }
      }
      
      // Hedef tarih ve saatteki diğer aktif randevuları kontrol et (tamamlanmış/iptal edilmiş randevular çakışma oluşturmaz)
      const targetDateAppointments = appointments.filter(apt => {
        let aptDate = apt.date;
        if (aptDate && typeof aptDate === 'string') {
          if (aptDate.includes('T')) {
            aptDate = aptDate.split('T')[0];
          }
          if (aptDate.length === 4) {
            aptDate = targetDate;
          }
        }
        return aptDate === targetDate && 
               apt.id.toString() !== appointment.id.toString() &&
               (apt.status === 'pending' || apt.status === 'confirmed'); // Sadece aktif randevular
      });
      
      // Yeni randevunun zaman aralığını hesapla
      const newStartTime = new Date(`2000-01-01T${targetTime}`);
      const newEndTime = new Date(newStartTime.getTime() + duration * 60000);
      
      // Diğer randevularla çakışma kontrolü
      for (const existingApt of targetDateAppointments) {
        // Mevcut randevunun süresini hesapla
        let existingDuration = 60;
        if (existingApt.serviceIds) {
          try {
            const serviceIds = typeof existingApt.serviceIds === 'string' ? 
              JSON.parse(existingApt.serviceIds) : existingApt.serviceIds;
            
            if (Array.isArray(serviceIds)) {
              const selectedServices = services.filter(service => 
                serviceIds.some((id: any) => id.toString() === service.id.toString())
              );
              existingDuration = selectedServices.reduce((total, service) => 
                total + (service.duration || 60), 0
              );
            }
          } catch (error) {
            existingDuration = 60;
          }
        }
        
        const existingStartTime = new Date(`2000-01-01T${formatTime(existingApt.time)}`);
        const existingEndTime = new Date(existingStartTime.getTime() + existingDuration * 60000);
        
        // Çakışma kontrolü (15 dakika tolerans)
        const overlapStart = new Date(Math.max(newStartTime.getTime(), existingStartTime.getTime()));
        const overlapEnd = new Date(Math.min(newEndTime.getTime(), existingEndTime.getTime()));
        const overlapMinutes = (overlapEnd.getTime() - overlapStart.getTime()) / 60000;
        
        if (overlapMinutes > 15) {
          return true; // Çakışma var
        }
      }
      
      return false; // Çakışma yok
    } catch (error) {
      console.error('Error checking conflict:', error);
      return false;
    }
  };

  // Handle appointment drag end
  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (!over) {
      return;
    }
    
    if (active.id === over.id) {
      return;
    }
    
    const draggedAppointmentId = active.id;
    const targetSlotId = over.id as string;
    
    // Parse target slot info
    const parts = targetSlotId.split('-');
    
    // Slot ID format: YYYY-MM-DD-HH:MM
    // So we need to reconstruct date and time properly
    if (parts.length < 4) {
      console.error('Invalid slot ID format:', targetSlotId);
      return;
    }
    
    const targetDate = `${parts[0]}-${parts[1]}-${parts[2]}`;
    const targetTime = parts[3];
    
    // Find the dragged appointment
    const draggedAppointment = appointments.find(apt => apt.id.toString() === draggedAppointmentId);
    
    if (!draggedAppointment) {
      return;
    }
    
    // Çakışma kontrolü yap
    const hasConflict = await checkAppointmentConflict(draggedAppointment, targetDate, targetTime);
    
    if (hasConflict) {
      // Çakışma varsa kullanıcıya uyarı göster
      setError('Bu saatte başka bir randevu bulunuyor. Lütfen farklı bir saat seçin.');
      return;
    }
    
    // Update appointment time
    try {
      // Randevunun orijinal tarihini koru, sadece saati değiştir
      let originalDate = draggedAppointment.date;
      
      // Tarih formatını normalize et
      if (originalDate && typeof originalDate === 'string') {
        if (originalDate.includes('T')) {
          originalDate = originalDate.split('T')[0];
        }
        // Sadece yıl gelmişse (2025 gibi), bugünün tarihini kullan
        if (originalDate.length === 4) {
          originalDate = format(new Date(), 'yyyy-MM-dd');
        }
      }
      
      // Saat formatını düzelt - HH:MM:SS formatında olmalı
      const formattedTime = targetTime + ':00';
      
      const updatedAppointment = {
        ...draggedAppointment,
        date: originalDate,
        time: formattedTime
      };
      
      await ApiService.updateAppointment(draggedAppointmentId, updatedAppointment);
      
      // State'i anında güncelle
      setAppointments(prevAppointments => 
        prevAppointments.map(apt => 
          apt.id.toString() === draggedAppointmentId 
            ? { ...apt, date: originalDate, time: formattedTime }
            : apt
        )
      );
      
      // Timeline'ı zorla yenile
      setLastUpdateTime(new Date());
      
      setError(null); // Başarılı olursa hata mesajını temizle
    } catch (error) {
      console.error('Error updating appointment:', error);
      setError('Randevu güncellenemedi: ' + (error as any).message);
    }
  };

  const handleCreateAppointment = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // E-posta validasyonu
    if (!isValidEmail(formData.customerEmail)) {
      setError('Lütfen geçerli bir e-posta adresi girin (örn: ornek@email.com).');
      return;
    }
    
    // Telefon validasyonu
    if (!isValidPhone(formData.customerPhone)) {
      setError('Lütfen geçerli bir telefon numarası girin (10 veya 11 haneli).');
      return;
    }
    
    try {
      const selectedServices = services.filter(s => formData.serviceIds.includes(s.id.toString()));
      const totalPrice = selectedServices.reduce((sum, service) => sum + (typeof service.price === 'string' ? parseFloat(service.price) : service.price), 0);
      
      // Saat formatını HH:MM olarak düzelt
      const timeFormatted = formData.time.includes(':') ? formData.time.substring(0, 5) : formData.time;
      
      const appointmentData = {
        customerName: formData.customerName,
        customerEmail: formData.customerEmail,
        customerPhone: formData.customerPhone,
        serviceIds: formData.serviceIds,
        serviceNames: selectedServices.map(s => s.name),
        teamMemberId: formData.teamMemberId ? parseInt(formData.teamMemberId) : null,
        date: formData.date,
        time: timeFormatted,
        notes: formData.notes,
        totalPrice: formData.totalPrice || totalPrice.toString(),
        status: formData.status,
        customDuration: formData.customDuration ? parseInt(formData.customDuration) : null
      };

      // Check for conflicts
      const conflictCheck = await checkForConflicts(appointmentData);
      
      if (conflictCheck.hasConflict) {
        setConflictData(conflictCheck);
        setPendingAppointmentData(appointmentData);
        setShowConflictModal(true);
        return;
      }

      const newAppointment = await ApiService.createAppointment(appointmentData);
      setShowCreateModal(false);
      resetForm();
      
      // WebSocket broadcast ile gelen veriyi bekleyin
      // Manuel state güncellemesi kaldırıldı - sadece WebSocket'ten gelen veri kullanılacak
      
      if (onAppointmentsChange) {
        onAppointmentsChange();
      }
    } catch (error: any) {
      console.error('Randevu oluşturulurken hata:', error);
      setError('Randevu oluşturulamadı: ' + error.message);
    }
  };

  const handleEditAppointment = (appointment: Appointment) => {
    setEditingAppointment(appointment);
    
    // Mevcut hizmetleri yükle (eğer yüklenmemişse)
    if (services.length === 0) {
      loadServices();
    }
    
    // Çoklu hizmet desteği - serviceIds ve serviceNames'i doğru şekilde ayarla
    let serviceIds: string[] = [];
    let serviceNames: string[] = [];
    
    // serviceIds parsing
    if (appointment.serviceIds && Array.isArray(appointment.serviceIds)) {
      serviceIds = appointment.serviceIds.map((id: any) => id.toString());

    } else if (appointment.serviceIds && typeof appointment.serviceIds === 'string') {
      // String olarak JSON geliyorsa parse et
      try {
        const parsed = JSON.parse(appointment.serviceIds);
        if (Array.isArray(parsed)) {
          serviceIds = parsed.map((id: any) => id.toString());

        }
      } catch (e) {
        console.error('Error parsing serviceIds JSON:', e);
      }
    }
    
    // serviceNames parsing
    if (appointment.serviceNames && Array.isArray(appointment.serviceNames)) {
      serviceNames = appointment.serviceNames;

    } else if (appointment.serviceNames && typeof appointment.serviceNames === 'string') {
      // String olarak JSON geliyorsa parse et
      try {
        const parsed = JSON.parse(appointment.serviceNames);
        if (Array.isArray(parsed)) {
          serviceNames = parsed;

        }
      } catch (e) {
        console.error('Error parsing serviceNames JSON:', e);
      }
    }
    
    // Saat formatını HH:MM olarak düzelt
    const timeFormatted = appointment.time && appointment.time.includes(':') ? appointment.time.substring(0, 5) : appointment.time;
    
    // Mevcut randevunun süresini hesapla
    let currentDuration = 60;
    if (serviceIds && serviceIds.length > 0) {
      const selectedServices = services.filter(service => 
        serviceIds.includes(service.id.toString())
      );
      currentDuration = selectedServices.reduce((total, service) => 
        total + (service.duration || 60), 0
      );
    }
    
    console.log('=== EDIT APPOINTMENT DEBUG ===');
    console.log('Appointment customDuration:', appointment.customDuration);
    console.log('Appointment customDuration type:', typeof appointment.customDuration);
    console.log('Calculated currentDuration:', currentDuration);
    console.log('Final customDuration value:', appointment.customDuration ? appointment.customDuration.toString() : currentDuration.toString());
    
    setFormData({
      customerName: appointment.customerName,
      customerEmail: appointment.customerEmail,
      customerPhone: appointment.customerPhone,
      serviceIds: serviceIds,
      serviceNames: serviceNames,
      teamMemberId: appointment.teamMemberId ? appointment.teamMemberId.toString() : '',
      date: appointment.date,
      time: timeFormatted,
      notes: appointment.notes || '',
      totalPrice: appointment.totalPrice || '',
      status: appointment.status,
      customDuration: appointment.customDuration ? appointment.customDuration.toString() : currentDuration.toString() // Özel süre varsa onu, yoksa hesaplanan süreyi göster
    });
    setShowEditModal(true);
  };

  const handleUpdateAppointment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingAppointment) return;

    // E-posta validasyonu
    if (!isValidEmail(formData.customerEmail)) {
      setError('Lütfen geçerli bir e-posta adresi girin (örn: ornek@email.com).');
      return;
    }
    
    // Telefon validasyonu
    if (!isValidPhone(formData.customerPhone)) {
      setError('Lütfen geçerli bir telefon numarası girin (10 veya 11 haneli).');
      return;
    }

    try {
      // ID'yi en başta saklayalım
      const appointmentId = editingAppointment.id;
      
      const selectedServices = services.filter(s => formData.serviceIds.includes(s.id.toString()));
      const totalPrice = selectedServices.reduce((sum, service) => sum + (typeof service.price === 'string' ? parseFloat(service.price) : service.price), 0);
      
      // Saat formatını HH:MM olarak düzelt
      const timeFormatted = formData.time.includes(':') ? formData.time.substring(0, 5) : formData.time;
      
      const appointmentData = {
        customerName: formData.customerName,
        customerEmail: formData.customerEmail,
        customerPhone: formData.customerPhone,
        serviceIds: formData.serviceIds,
        serviceNames: selectedServices.map(s => s.name),
        teamMemberId: formData.teamMemberId ? parseInt(formData.teamMemberId) : null,
        date: formData.date,
        time: timeFormatted,
        notes: formData.notes,
        totalPrice: formData.totalPrice || totalPrice.toString(),
        status: formData.status,
        customDuration: formData.customDuration ? parseInt(formData.customDuration) : null
      };

      console.log('=== FRONTEND UPDATE DEBUG ===');
      console.log('formData.customDuration:', formData.customDuration);
      console.log('formData.customDuration type:', typeof formData.customDuration);
      console.log('Parsed customDuration:', formData.customDuration ? parseInt(formData.customDuration) : null);
      console.log('Full appointmentData:', appointmentData);

      // Check for conflicts (exclude current appointment)
      console.log('Debug appointmentId:', appointmentId, 'Type:', typeof appointmentId);
      const conflictCheck = await checkForConflicts(appointmentData, appointmentId);
      
      if (conflictCheck.hasConflict) {
        setConflictData(conflictCheck);
        setPendingAppointmentData(appointmentData);
        setShowConflictModal(true);
        return;
      }

      const updatedAppointment = await ApiService.updateAppointment(appointmentId.toString(), appointmentData);
      
      // Modal'ı kapat
      setShowEditModal(false);
      setEditingAppointment(null);
      resetForm();
      
      // State'i anında güncelle
      setAppointments(prevAppointments => {
        const updatedList = prevAppointments.map(apt => {
          if (apt.id === appointmentId) {
            return {
              ...apt,
              ...appointmentData,
              service: appointmentData.serviceNames.join(', '),
              teamMember: teamMembers.find(tm => tm.id === appointmentData.teamMemberId)
            };
          }
          return apt;
        });
        return updatedList;
      });
      
      // Timeline'ı zorla yenile
      setLastUpdateTime(new Date());
      
      if (onAppointmentsChange) {
        onAppointmentsChange();
      }
    } catch (error: any) {
      console.error('Randevu güncellenirken hata:', error);
      setError('Randevu güncellenemedi: ' + error.message);
    }
  };

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5, // 5px hareket ile drag başlar
        tolerance: 3, // 3px tolerans
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Draggable Appointment Component
  const DraggableAppointment = ({ appointment, slot, rowIndex = 0, totalSlots = 20, startHour = timelineStartHour }: { appointment: Appointment; slot: any; rowIndex?: number; totalSlots?: number; startHour?: number }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ 
      id: appointment.id.toString(),
      data: {
        type: 'appointment',
        appointment: appointment,
      },
    });

    // Debug: isMainSlot kontrolü
    console.log('DraggableAppointment - appointment:', appointment.id, 'isMainSlot:', (appointment as any).isMainSlot);
    
    // Sadece ana slot'ta göster (isMainSlot: true)
    if (!(appointment as any).isMainSlot) {
      console.log('Skipping appointment:', appointment.id, 'not main slot');
      return null;
    }

    // Randevunun süresini hesapla
    let duration = 60;
    
    // Önce customDuration kontrolü
    if (appointment.customDuration && appointment.customDuration > 0) {
      duration = appointment.customDuration;
    } else if (appointment.serviceIds) {
      try {
        const serviceIds = typeof appointment.serviceIds === 'string' ? 
          JSON.parse(appointment.serviceIds) : appointment.serviceIds;
        
        if (Array.isArray(serviceIds)) {
          const stringServiceIds = serviceIds.map(id => id.toString());
          const selectedServices = services.filter(service => 
            stringServiceIds.includes(service.id.toString())
          );
          
          duration = selectedServices.reduce((total, service) => 
            total + (service.duration || 60), 0
          );
        }
      } catch (error) {
        console.error('Error parsing serviceIds:', error);
        duration = 60;
      }
    }
    
    const slotsNeeded = Math.ceil(duration / 30); // 30 dakikalık slotlar için süreye göre slot sayısı hesapla
    


    const style: React.CSSProperties = {
      transform: CSS.Transform.toString(transform),
      transition: transition || 'transform 0.2s ease-in-out',
      opacity: isDragging ? 0.8 : 1,
      zIndex: isDragging ? 1000 : 'auto',
      willChange: isDragging ? 'transform' : 'auto',
    };

    return (
      <div
        ref={setNodeRef}
        className={`border rounded p-1 transition-all duration-200 hover:shadow-lg transform-gpu select-none relative ${
          isDragging 
            ? 'shadow-2xl scale-105 rotate-2 border-blue-400' 
            : ''
        }`}
        style={{
          ...style,
          pointerEvents: 'auto',
          userSelect: 'none',
          height: '100px', // Tüm kartlar aynı yükseklikte
          width: `${slotsNeeded * (100/totalSlots)}%`, // Dinamik genişlik hesaplama
          position: 'absolute',
          left: `${((parseInt(slot.time.split(':')[0]) - startHour) * 2 + (parseInt(slot.time.split(':')[1]) / 30)) * (100/totalSlots)}%`, // Dinamik pozisyon hesaplama
          top: `${40 + (rowIndex * 110)}px`, // Her satır için 110px boşluk
          zIndex: isDragging ? 1000 : 1,
          backgroundColor: isDragging ? homeSettings?.backgroundColor || '#000000' : homeSettings?.secondaryBackgroundColor || '#1f2937',
          borderColor: homeSettings?.secondaryBackgroundColor || '#374151',
        }}
      >
        <div className="flex items-center justify-between h-full">
          <div className="flex items-center space-x-1 flex-1 min-w-0">
            <div 
              {...attributes}
              {...listeners}
              className="cursor-grab active:cursor-grabbing hover:bg-gray-600 p-0.5 rounded"
            >
              <GripVertical className="w-2.5 h-2.5 text-gray-400 flex-shrink-0" />
            </div>
            <div className="min-w-0 flex-1 overflow-hidden">
              <div className="font-medium text-xs text-white truncate">{appointment.customerName}</div>
              <div className="text-xs text-gray-400 truncate">{appointment.service}</div>
              <div className="text-xs text-blue-400 font-medium truncate">
                {appointment.teamMember?.name || 'Çalışan Yok'}
              </div>
              <div className="text-xs text-gray-500 truncate">
                {appointment.time} - {(() => {
                  // Aynı duration hesaplamasını kullan
                  let duration = 60;
                  
                  if (appointment.customDuration && appointment.customDuration > 0) {
                    duration = appointment.customDuration;
                  } else if (appointment.serviceIds) {
                    try {
                      const serviceIds = typeof appointment.serviceIds === 'string' ? 
                        JSON.parse(appointment.serviceIds) : appointment.serviceIds;
                      
                      if (Array.isArray(serviceIds)) {
                        const stringServiceIds = serviceIds.map(id => id.toString());
                        const selectedServices = services.filter(service => 
                          stringServiceIds.includes(service.id.toString())
                        );
                        
                        duration = selectedServices.reduce((total, service) => 
                          total + (service.duration || 60), 0
                        );
                      }
                    } catch (error) {
                      duration = 60;
                    }
                  }
                  
                  const startTime = new Date(`2000-01-01T${appointment.time}`);
                  const endTime = new Date(startTime.getTime() + duration * 60000);
                  return endTime.toTimeString().substring(0, 5);
                })()}
              </div>
              {/* Not butonu */}
              {appointment.notes && appointment.notes.trim() && (
                <button
                  className="text-xs text-yellow-400 hover:text-yellow-300 transition-colors flex items-center"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedAppointmentNotes({
                      customerName: appointment.customerName,
                      notes: appointment.notes || '',
                      date: appointment.date,
                      time: appointment.time
                    });
                    setShowNotesModal(true);
                  }}
                  title="Notu görüntülemek için tıklayın"
                >
                  <span className="mr-1">📝</span>
                  <span>Not</span>
                </button>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-1 flex-shrink-0">
            <div className={`w-1.5 h-1.5 rounded-full ${
              appointment.status === 'confirmed' ? 'bg-green-500' :
              appointment.status === 'pending' ? 'bg-yellow-500' :
              appointment.status === 'completed' ? 'bg-blue-500' : 'bg-red-500'
            }`} />
          </div>
        </div>
      </div>
    );
  };

  // Time Slot Component - Sadece grid çizgileri için
  const TimeSlot = ({ slot }: { slot: any }) => {
    const { setNodeRef, isOver } = useDroppable({
      id: slot.id,
      data: {
        accepts: ['appointment'],
        slot: slot,
      },
    });

    return (
      <div 
        ref={setNodeRef}
        className={`border-r p-2 transition-all duration-200 relative min-h-[120px] ${
          isOver ? 'bg-blue-800/30' : ''
        }`}
        style={{
          minHeight: '100px',
          borderColor: homeSettings?.secondaryBackgroundColor || '#374151',
          backgroundColor: isOver ? 'rgba(59, 130, 246, 0.3)' : homeSettings?.backgroundColor || '#000000',
        }}
      >
        <div className="font-bold text-center text-xs text-white py-1 border-b border-gray-600">
          {slot.time}
        </div>
        {isOver && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-blue-400 font-medium text-xs">
              Buraya Bırak
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6 text-white">
      {/* Header with Stats */}
      <div className="rounded-lg p-6 border" style={{ backgroundColor: homeSettings?.secondaryBackgroundColor || '#1f2937', borderColor: homeSettings?.secondaryBackgroundColor || '#374151' }}>
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <div>
            <h2 className="text-2xl font-bold text-white">Randevu Yönetimi</h2>
            <p className="text-gray-400 mt-1">Tüm randevuları yönetin ve takip edin</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center px-4 py-2 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-lg hover:from-pink-600 hover:to-purple-700 transition-all duration-200 shadow-lg"
          >
            <Plus className="w-4 h-4 mr-2" />
            Yeni Randevu
          </button>
        </div>
        
        {/* Statistics Cards */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mt-6">
          <div className="rounded-lg p-4 border" style={{ backgroundColor: homeSettings?.backgroundColor || '#000000', borderColor: homeSettings?.secondaryBackgroundColor || '#374151' }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Toplam</p>
                <p className="text-2xl font-bold text-white">{stats.total}</p>
              </div>
              <Calendar className="w-6 h-6 text-blue-400" />
            </div>
          </div>
          <div className="rounded-lg p-4 border" style={{ backgroundColor: homeSettings?.backgroundColor || '#000000', borderColor: homeSettings?.secondaryBackgroundColor || '#374151' }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Bugün</p>
                <p className="text-2xl font-bold text-white">{stats.today}</p>
              </div>
              <CalendarDays className="w-6 h-6 text-green-400" />
            </div>
          </div>
          <div className="rounded-lg p-4 border" style={{ backgroundColor: homeSettings?.backgroundColor || '#000000', borderColor: homeSettings?.secondaryBackgroundColor || '#374151' }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Bekleyen</p>
                <p className="text-2xl font-bold text-yellow-400">{stats.pending}</p>
              </div>
              <AlertCircle className="w-6 h-6 text-yellow-400" />
            </div>
          </div>
          <div className="rounded-lg p-4 border" style={{ backgroundColor: homeSettings?.backgroundColor || '#000000', borderColor: homeSettings?.secondaryBackgroundColor || '#374151' }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Onaylanan</p>
                <p className="text-2xl font-bold text-green-400">{stats.confirmed}</p>
              </div>
              <CheckCircle className="w-6 h-6 text-green-400" />
            </div>
          </div>
          <div className="rounded-lg p-4 border" style={{ backgroundColor: homeSettings?.backgroundColor || '#000000', borderColor: homeSettings?.secondaryBackgroundColor || '#374151' }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Tamamlanan</p>
                <p className="text-2xl font-bold text-blue-400">{stats.completed}</p>
              </div>
              <CheckCircle className="w-6 h-6 text-blue-400" />
            </div>
          </div>
          <div className="rounded-lg p-4 border" style={{ backgroundColor: homeSettings?.backgroundColor || '#000000', borderColor: homeSettings?.secondaryBackgroundColor || '#374151' }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">İptal</p>
                <p className="text-2xl font-bold text-red-400">{stats.cancelled}</p>
              </div>
              <XCircle className="w-6 h-6 text-red-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="rounded-lg p-6 border" style={{ backgroundColor: homeSettings?.secondaryBackgroundColor || '#1f2937', borderColor: homeSettings?.secondaryBackgroundColor || '#374151' }}>
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
          {/* Search */}
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Müşteri adı, email, telefon veya hizmet ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
              />
            </div>
          </div>

          {/* View Mode Toggle */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-2 rounded-lg transition-colors ${
                viewMode === 'list' 
                  ? 'bg-pink-500 text-white' 
                  : 'text-gray-400 hover:text-white'
              }`}
              style={viewMode !== 'list' ? { backgroundColor: homeSettings?.secondaryBackgroundColor || '#1f2937' } : {}}
            >
              Liste
            </button>
            <button
              onClick={() => setViewMode('calendar')}
              className={`px-3 py-2 rounded-lg transition-colors ${
                viewMode === 'calendar' 
                  ? 'bg-pink-500 text-white' 
                  : 'text-gray-400 hover:text-white'
              }`}
              style={viewMode !== 'calendar' ? { backgroundColor: homeSettings?.secondaryBackgroundColor || '#1f2937' } : {}}
            >
              Takvim
            </button>
          </div>

          {/* Filters Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 text-gray-300 rounded-lg transition-colors"
            style={{ backgroundColor: homeSettings?.secondaryBackgroundColor || '#1f2937' }}
          >
            <Filter className="w-4 h-4" />
            Filtreler
            {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Durum</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-pink-500"
                style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
              >
                <option value="all">Tüm Durumlar</option>
                <option value="pending">Bekliyor</option>
                <option value="confirmed">Onaylandı</option>
                <option value="completed">Tamamlandı</option>
                <option value="cancelled">İptal</option>
              </select>
            </div>

            {/* Date Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Tarih</label>
              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-pink-500"
                style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
              >
                <option value="all">Tüm Tarihler</option>
                <option value="today">Bugün</option>
                <option value="week">Bu Hafta</option>
                <option value="month">Bu Ay</option>
              </select>
            </div>

            {/* Team Member Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Çalışan</label>
              <select
                value={teamMemberFilter}
                onChange={(e) => setTeamMemberFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-pink-500"
                style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
              >
                <option value="all">Tüm Çalışanlar</option>
                {teamMembers.map((member) => (
                  <option key={member.id} value={member.id.toString()}>
                    {member.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Sort Options */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Sıralama</label>
              <div className="flex gap-2">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as 'date' | 'customer' | 'status')}
                  className="flex-1 px-3 py-2 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-pink-500"
                  style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
                >
                  <option value="date">Tarih</option>
                  <option value="customer">Müşteri</option>
                  <option value="status">Durum</option>
                </select>
                <button
                  onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                  className="px-3 py-2 rounded-lg text-white transition-colors"
                  style={{ backgroundColor: homeSettings?.secondaryBackgroundColor || '#1f2937', borderColor: homeSettings?.secondaryBackgroundColor || '#374151' }}
                >
                  {sortOrder === 'asc' ? '↑' : '↓'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Loading State */}
      {loading && (
        <div className="bg-gray-900 rounded-lg shadow-sm p-8 text-center border border-gray-800">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500 mx-auto mb-4"></div>
          <div className="text-gray-300">Randevular yükleniyor...</div>
        </div>
      )}
      
      {/* Error State */}
      {error && !loading && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="text-red-700">{error}</div>
          <button 
            onClick={loadAppointments}
            className="mt-2 px-3 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200"
          >
            Tekrar Dene
          </button>
        </div>
      )}
      
      {/* Content */}
      {!loading && !error && (
        <div className="rounded-lg shadow-sm overflow-hidden border" style={{ backgroundColor: homeSettings?.secondaryBackgroundColor || '#1f2937', borderColor: homeSettings?.secondaryBackgroundColor || '#374151' }}>
          {viewMode === 'list' ? (
            filteredAndSortedAppointments.length === 0 ? (
              <div className="p-12 text-center text-gray-400">
                <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-500" />
                <h3 className="text-xl font-medium mb-2">Randevu Bulunamadı</h3>
                <p className="text-gray-500 mb-4">
                  {searchTerm || statusFilter !== 'all' || dateFilter !== 'all' || teamMemberFilter !== 'all'
                    ? 'Arama kriterlerinize uygun randevu bulunamadı.'
                    : 'Henüz randevu bulunmuyor.'}
                </p>
                <button 
                  onClick={loadAppointments}
                  className="px-4 py-2 text-gray-300 rounded-lg transition-colors"
                  style={{ backgroundColor: homeSettings?.secondaryBackgroundColor || '#1f2937' }}
                >
                  Yenile
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-700">
                  <thead style={{ backgroundColor: homeSettings?.secondaryBackgroundColor || '#1f2937' }}>
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Müşteri Bilgileri
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Hizmet & Çalışan
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Tarih & Saat
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Fiyat
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Durum
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        İşlemler
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700" style={{ backgroundColor: homeSettings?.secondaryBackgroundColor || '#1f2937' }}>
                    {filteredAndSortedAppointments.map((appointment) => (
                      <tr key={appointment.id} className="transition-colors hover:bg-opacity-80" style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}>
                        <td className="px-6 py-4">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-gray-400" />
                              <span className="font-medium text-white">{appointment.customerName}</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-400">
                              <Mail className="w-4 h-4" />
                              <span>{appointment.customerEmail}</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-400">
                              <Phone className="w-4 h-4" />
                              <span>{appointment.customerPhone}</span>
                            </div>
                            {/* Not butonu */}
                            {appointment.notes && appointment.notes.trim() && (
                              <button
                                className="flex items-center gap-2 text-sm text-yellow-400 hover:text-yellow-300 transition-colors"
                                onClick={() => {
                                  setSelectedAppointmentNotes({
                                    customerName: appointment.customerName,
                                    notes: appointment.notes || '',
                                    date: appointment.date,
                                    time: appointment.time
                                  });
                                  setShowNotesModal(true);
                                }}
                                title="Notu görüntülemek için tıklayın"
                              >
                                <span>📝</span>
                                <span>Not Görüntüle</span>
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <Tag className="w-4 h-4 text-gray-400" />
                              <span className="text-sm text-white">{appointment.service}</span>
                            </div>
                            {appointment.teamMember && (
                              <div className="flex items-center gap-2">
                                <Users className="w-4 h-4 text-gray-400" />
                                <div>
                                  <span className="text-sm font-medium text-white">{appointment.teamMember.name}</span>
                                  <span className="text-xs text-gray-500 block">{appointment.teamMember.specialization}</span>
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-gray-400" />
                              <span className="text-sm text-white">{appointment.date}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4 text-gray-400" />
                              <span className="text-sm text-gray-400">{appointment.time}</span>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <DollarSign className="w-4 h-4 text-gray-400" />
                            <span className="text-sm font-medium text-white">
                              {appointment.totalPrice ? `₺${appointment.totalPrice}` : 'Belirtilmemiş'}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <select
                            value={appointment.status}
                            onChange={(e) => updateAppointmentStatus(appointment.id, e.target.value as Appointment['status'])}
                            className={`px-3 py-1 rounded-full text-xs font-medium border-0 cursor-pointer transition-colors ${getStatusColor(appointment.status)}`}
                          >
                            <option value="pending">Bekliyor</option>
                            <option value="confirmed">Onaylandı</option>
                            <option value="completed">Tamamlandı</option>
                            <option value="cancelled">İptal</option>
                          </select>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleEditAppointment(appointment)}
                              className="p-2 text-blue-400 hover:text-blue-300 hover:bg-blue-900/20 rounded-lg transition-colors"
                              title="Düzenle"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => deleteAppointment(appointment.id)}
                              className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded-lg transition-colors"
                              title="Sil"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : (
            <div className="p-6">
              <div className="space-y-6">
                {/* Calendar or Time Slots - Full Width */}
                <div className="w-full">
                  {isViewingDay ? (
                    <div className="rounded-lg p-6 border" style={{ backgroundColor: homeSettings?.secondaryBackgroundColor || '#1f2937', borderColor: homeSettings?.secondaryBackgroundColor || '#374151' }}>
                      <div className="flex items-center justify-between mb-6">
                        <div>
                          <h3 className="text-xl font-semibold text-white">
                            {selectedCalendarDate && format(selectedCalendarDate, 'dd MMMM yyyy', { locale: tr })}
                          </h3>
                          <p className="text-gray-400 text-sm">Saat Dilimleri</p>
                        </div>
                        <button
                          onClick={handleBackToCalendar}
                          className="flex items-center gap-2 px-4 py-2 text-white rounded-lg transition-colors"
                          style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
                        >
                          <ChevronUp className="w-4 h-4 rotate-90" />
                          Takvime Dön
                        </button>
                      </div>
                      
                      <DndContext
                        key={`dnd-context-${lastUpdateTime?.getTime()}-${appointments.length}-${timeSlots.length}`}
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                      >
                        <SortableContext 
                          key={`sortable-context-${lastUpdateTime?.getTime()}-${appointments.length}-${timeSlots.length}`}
                          items={timeSlots.flatMap(slot => 
                            slot.appointments.map((apt: any) => apt.id.toString())
                          )}
                          strategy={rectSortingStrategy}
                        >
                                                  <div key={`timeline-grid-${lastUpdateTime?.getTime()}-${appointments.length}-${timeSlots.length}`} className="relative border rounded-lg overflow-x-auto w-full" style={{ minHeight: '300px', height: '700px', minWidth: '1200px', borderColor: homeSettings?.secondaryBackgroundColor || '#374151' }}>
                          {/* Grid çizgileri için arka plan */}
                          <div className={`grid gap-0 h-full`} style={{ 
                            minWidth: '1200px',
                            gridTemplateColumns: `repeat(${timeSlots.length}, 1fr)`
                          }}>
                            {timeSlots.map((slot) => (
                              <TimeSlot key={`slot-${slot.id}-${slot.appointments.length}-${lastUpdateTime?.getTime()}`} slot={slot} />
                            ))}
                          </div>
                          
                          {/* Randevuları absolute positioning ile yerleştir */}
                          <div className="absolute inset-0">
                            {(() => {
                              const appointmentRows = distributeAppointmentsToRows(timeSlots);
                              const allAppointments: Array<{appointment: Appointment, slot: any, rowIndex: number}> = [];
                              
                              Object.keys(appointmentRows).forEach(rowKey => {
                                const rowIndex = parseInt(rowKey.split('-')[1] || '0');
                                console.log('Processing rowKey:', rowKey, 'rowIndex:', rowIndex, 'appointments:', appointmentRows[rowKey].length);
                                
                                appointmentRows[rowKey].forEach((appointment: Appointment) => {
                                  // Her randevu için kendi başlangıç slot'unu bul
                                  // Zaman formatını düzelt (09:00:00 -> 09:00)
                                  const formattedTime = appointment.time.split(':').slice(0, 2).join(':');
                                  const appointmentSlot = timeSlots.find(s => s.time === formattedTime);
                                  console.log('Looking for slot with time:', formattedTime, 'found:', !!appointmentSlot);
                                  
                                  if (appointmentSlot) {
                                    allAppointments.push({ appointment, slot: appointmentSlot, rowIndex });
                                    console.log('Added appointment to allAppointments:', appointment.id, 'rowIndex:', rowIndex);
                                  }
                                });
                              });
                              
                              console.log('Rendering appointments:', allAppointments.length, 'appointments');
                              return allAppointments.map(({ appointment, slot, rowIndex }) => {
                                console.log('Rendering appointment:', appointment.id, 'time:', appointment.time, 'customer:', appointment.customerName);
                                return (
                                  <DraggableAppointment 
                                    key={`draggable-${appointment.id}-${appointment.time}-${appointment.customerName}-${lastUpdateTime?.getTime()}`}
                                    appointment={appointment} 
                                    slot={slot} 
                                    rowIndex={rowIndex}
                                    totalSlots={timeSlots.length}
                                  />
                                );
                              });
                            })()}
                          </div>
                        </div>
                        </SortableContext>
                      </DndContext>
                    </div>
                  ) : (
                    <div className="rounded-lg p-6 border" style={{ backgroundColor: homeSettings?.secondaryBackgroundColor || '#1f2937', borderColor: homeSettings?.secondaryBackgroundColor || '#374151' }}>
                      <ReactCalendar
                        onChange={(date) => {
                          setCalendarDate(date as Date);
                          handleCalendarDateClick(date as Date);
                        }}
                        value={calendarDate}
                        tileContent={tileContent}
                        className="react-calendar w-full"
                        locale="tr"
                      />
                    </div>
                  )}
                </div>
                
                {/* Selected Date Appointments - Full Width Below */}
                {selectedCalendarDate && (
                  <div key={`timeline-container-${lastUpdateTime?.getTime()}-${appointments.length}-${appointments.map(a => a.id + a.time).join('')}`} className="rounded-lg p-6 border" style={{ backgroundColor: homeSettings?.secondaryBackgroundColor || '#1f2937', borderColor: homeSettings?.secondaryBackgroundColor || '#374151' }}>
                    <h3 className="text-lg font-semibold text-white mb-4">
                      {format(selectedCalendarDate, 'dd MMMM yyyy', { locale: tr })} - Randevular
                      {lastUpdateTime && (
                        <span className="text-xs text-gray-500 ml-2">
                          (Son güncelleme: {lastUpdateTime.toLocaleTimeString('tr-TR')})
                        </span>
                      )}
                    </h3>
                    
                    {getAppointmentsForDate(selectedCalendarDate).length === 0 ? (
                      <p className="text-gray-400 text-sm">Bu tarihte randevu bulunmuyor.</p>
                    ) : (
                      <div key={`timeline-${lastUpdateTime?.getTime()}-${appointments.length}-${JSON.stringify(getAppointmentsForDate(selectedCalendarDate).map(a => a.id + a.time))}`} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {getAppointmentsForDate(selectedCalendarDate).map((appointment) => (
                          <div key={`card-${appointment.id}-${appointment.time}-${appointment.customerName}-${lastUpdateTime?.getTime()}`} className="rounded-lg p-4 border transition-colors" style={{ backgroundColor: homeSettings?.backgroundColor || '#000000', borderColor: homeSettings?.secondaryBackgroundColor || '#374151' }}>
                            <div className="flex items-center justify-between mb-3">
                              <span className="font-medium text-white text-sm">{appointment.customerName}</span>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(appointment.status)}`}>
                                {appointment.status === 'pending' ? 'Bekliyor' : 
                                 appointment.status === 'confirmed' ? 'Onaylandı' :
                                 appointment.status === 'completed' ? 'Tamamlandı' : 'İptal'}
                              </span>
                            </div>
                            <div className="space-y-2 text-xs text-gray-300 mb-4">
                              <div className="flex items-center gap-2">
                                <Clock className="w-3 h-3" />
                                <span>{formatTime(appointment.time)} {/* Updated: {lastUpdateTime?.toLocaleTimeString()} */}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Tag className="w-3 h-3" />
                                <span className="truncate">{appointment.service}</span>
                              </div>
                              {appointment.teamMember && (
                                <div className="flex items-center gap-2">
                                  <Users className="w-3 h-3" />
                                  <span>{appointment.teamMember.name}</span>
                                </div>
                              )}
                              {appointment.totalPrice && (
                                <div className="flex items-center gap-2">
                                  <DollarSign className="w-3 h-3" />
                                  <span>₺{appointment.totalPrice}</span>
                                </div>
                              )}
                              {/* Not butonu */}
                              {appointment.notes && appointment.notes.trim() && (
                                <button
                                  className="flex items-center gap-1 text-yellow-400 hover:text-yellow-300 transition-colors text-xs"
                                  onClick={() => {
                                    setSelectedAppointmentNotes({
                                      customerName: appointment.customerName,
                                      notes: appointment.notes || '',
                                      date: appointment.date,
                                      time: appointment.time
                                    });
                                    setShowNotesModal(true);
                                  }}
                                  title="Notu görüntülemek için tıklayın"
                                >
                                  <span>📝</span>
                                  <span>Not</span>
                                </button>
                              )}
                              <div className="flex items-center gap-2">
                                <Clock className="w-3 h-3" />
                                <span>
                                  {formatTime(appointment.time)} - {(() => {
                                    // Debug: appointment verisini kontrol et
                                    console.log('=== TIMELINE CARD DEBUG ===');
                                    console.log('Appointment ID:', appointment.id);
                                    console.log('Appointment customDuration:', appointment.customDuration);
                                    console.log('Appointment customDuration type:', typeof appointment.customDuration);
                                    console.log('Full appointment data:', appointment);
                                    
                                    // Randevunun süresini hesapla
                                    let duration = 60;
                                    
                                    // Önce özel süre kontrolü (randevu verisinde)
                                    if (appointment.customDuration && appointment.customDuration > 0) {
                                      duration = appointment.customDuration;
                                      console.log('Using customDuration:', duration);
                                    } else if (appointment.serviceIds) {
                                      try {
                                        const serviceIds = typeof appointment.serviceIds === 'string' ? 
                                          JSON.parse(appointment.serviceIds) : appointment.serviceIds;
                                        
                                        if (Array.isArray(serviceIds)) {
                                          // serviceIds array'indeki tüm değerleri string'e çevir
                                          const stringServiceIds = serviceIds.map(id => id.toString());
                                          const selectedServices = services.filter(service => 
                                            stringServiceIds.includes(service.id.toString())
                                          );
                                          
                                          duration = selectedServices.reduce((total, service) => 
                                            total + (service.duration || 60), 0
                                          );
                                        }
                                      } catch (error) {
                                        console.error('Error parsing serviceIds:', error);
                                        duration = 60;
                                      }
                                    }
                                    
                                    // Bitiş saatini hesapla
                                    const formattedTime = formatTime(appointment.time);
                                    const startTime = new Date(`2000-01-01T${formattedTime}:00`);
                                    const endTime = new Date(startTime.getTime() + duration * 60000);
                                    
                                    return endTime.toTimeString().substring(0, 5);
                                  })()}
                                </span>
                              </div>
                            </div>
                            <div className="space-y-2">
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleEditAppointment(appointment)}
                                  className="flex-1 px-3 py-2 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors"
                                >
                                  Düzenle
                                </button>
                                <button
                                  onClick={() => deleteAppointment(appointment.id)}
                                  className="flex-1 px-3 py-2 bg-red-600 text-white text-xs rounded hover:bg-red-700 transition-colors"
                                >
                                  Sil
                                </button>
                              </div>
                              {appointment.status === 'pending' && (
                                <button
                                  onClick={() => updateAppointmentStatus(appointment.id, 'confirmed')}
                                  className="w-full px-3 py-2 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition-colors"
                                >
                                  Randevuyu Onayla
                                </button>
                              )}
                              {appointment.status === 'confirmed' && (
                                <button
                                  onClick={() => updateAppointmentStatus(appointment.id, 'completed')}
                                  className="w-full px-3 py-2 bg-purple-600 text-white text-xs rounded hover:bg-purple-700 transition-colors"
                                >
                                  Randevu Tamamlandı
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Yeni Randevu Oluşturma Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="rounded-lg p-6 w-full max-w-4xl text-white" style={{ backgroundColor: homeSettings?.secondaryBackgroundColor || '#1f2937' }}>
            <h3 className="text-lg font-semibold mb-4">Yeni Randevu Oluştur</h3>
            <form onSubmit={handleCreateAppointment} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Müşteri Adı</label>
                  <input
                    type="text"
                    value={formData.customerName}
                    onChange={(e) => setFormData({...formData, customerName: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 text-white"
                    style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">E-posta</label>
                  <input
                    type="email"
                    value={formData.customerEmail}
                    onChange={(e) => setFormData({...formData, customerEmail: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 text-white"
                    style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Telefon</label>
                  <input
                    type="tel"
                    value={formData.customerPhone}
                    onChange={(e) => setFormData({...formData, customerPhone: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 text-white"
                    style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Çalışan</label>
                  <select
                    value={formData.teamMemberId}
                    onChange={(e) => {
                      setFormData({...formData, teamMemberId: e.target.value});
                      // Ekip üyesi değiştiğinde datetime picker'ı da güncelle
                      if (formData.date) {
                        loadAvailableTimeSlots(formData.date, e.target.value);
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 text-white"
                    style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
                    required
                  >
                    <option value="">Çalışan seçin</option>
                    {teamMembers.map((member) => (
                      <option key={member.id} value={member.id}>
                        {member.name} - {member.specialization}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Hizmetler</label>
                <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-300 rounded-md p-2" style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}>
                  {services.length === 0 ? (
                    <div className="text-sm text-gray-400">Hizmet yükleniyor...</div>
                  ) : (
                    services.map((service) => (
                      <label key={service.id} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.serviceIds.includes(service.id.toString())}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData(prev => ({
                                ...prev,
                                serviceIds: [...prev.serviceIds, service.id.toString()],
                                serviceNames: [...prev.serviceNames, service.name]
                              }));
                            } else {
                              setFormData(prev => ({
                                ...prev,
                                serviceIds: prev.serviceIds.filter(id => id !== service.id.toString()),
                                serviceNames: prev.serviceNames.filter(name => name !== service.name)
                              }));
                            }
                          }}
                          className="w-4 h-4 text-pink-600 border-gray-300 rounded focus:ring-pink-500"
                        />
                        <span className="text-sm text-white">{service.name} - ₺{service.price}</span>
                      </label>
                    ))
                  )}
                </div>
                {formData.serviceIds.length > 0 && (
                  <div className="mt-1 text-xs text-gray-400">
                    Seçilen: {formData.serviceIds.length} hizmet
                  </div>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Tarih & Saat</label>
                  <button
                    type="button"
                    onClick={openDateTimePicker}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 text-left text-white transition-colors"
                    style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
                  >
                    {formData.date && formData.time ? (
                      <span className="text-white">
                        {new Date(formData.date).toLocaleDateString('tr-TR')} - {formData.time}
                      </span>
                    ) : (
                      <span className="text-gray-400">Tarih ve saat seçin</span>
                    )}
                  </button>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Fiyat</label>
                  <input
                    type="number"
                    value={formData.totalPrice}
                    onChange={(e) => setFormData({...formData, totalPrice: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 text-white"
                    style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
                    placeholder="Otomatik doldurulacak"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Notlar</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 text-white"
                  style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
                  rows={3}
                />
              </div>
              
              {/* Çakışma Uyarısı */}
              {createConflictData && (
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                  <div className="flex items-start">
                    <AlertCircle className="w-5 h-5 text-red-500 mr-2 mt-0.5" />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-red-800 mb-2">
                        ⚠️ {createConflictData.message?.includes('dolu') ? 'Dolu Saat Seçildi' : 'Çakışma Tespit Edildi'}
                      </div>
                      <div className="text-sm text-red-700 mb-2">
                        {createConflictData.message || 'Seçilen tarih ve saatte çakışan randevular bulunmaktadır.'}
                      </div>
                      {createConflictData.conflicts && createConflictData.conflicts.length > 0 && (
                        <div className="text-xs text-red-600">
                          Çakışan randevular: {createConflictData.conflicts.length} adet
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-pink-600 text-white py-2 px-4 rounded-md hover:bg-pink-700 transition-colors"
                >
                  Oluştur
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 transition-colors"
                >
                  İptal
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Randevu Düzenleme Modal */}
      {showEditModal && editingAppointment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="rounded-lg p-6 w-full max-w-4xl text-white" style={{ backgroundColor: homeSettings?.secondaryBackgroundColor || '#1f2937' }}>
            <h3 className="text-lg font-semibold mb-4">Randevu Düzenle</h3>
            <form onSubmit={handleUpdateAppointment} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Müşteri Adı</label>
                  <input
                    type="text"
                    value={formData.customerName}
                    onChange={(e) => setFormData({...formData, customerName: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 text-white"
                    style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">E-posta</label>
                  <input
                    type="email"
                    value={formData.customerEmail}
                    onChange={(e) => setFormData({...formData, customerEmail: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 text-white"
                    style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Telefon</label>
                  <input
                    type="tel"
                    value={formData.customerPhone}
                    onChange={(e) => setFormData({...formData, customerPhone: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 text-white"
                    style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Çalışan</label>
                  <select
                    value={formData.teamMemberId}
                    onChange={(e) => setFormData({...formData, teamMemberId: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 text-white"
                    style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
                    required
                  >
                    <option value="">Çalışan seçin</option>
                    {teamMembers.map((member) => (
                      <option key={member.id} value={member.id}>
                        {member.name} - {member.specialization}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Hizmetler</label>
                <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-300 rounded-md p-2">
                  {services.length === 0 ? (
                    <div className="text-sm text-gray-500">Hizmet yükleniyor...</div>
                  ) : (
                    services.map((service) => {
                      const isChecked = formData.serviceIds.includes(service.id.toString());

                      return (
                        <label key={service.id} className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFormData(prev => ({
                                  ...prev,
                                  serviceIds: [...prev.serviceIds, service.id.toString()],
                                  serviceNames: [...prev.serviceNames, service.name]
                                }));
                              } else {
                                setFormData(prev => ({
                                  ...prev,
                                  serviceIds: prev.serviceIds.filter(id => id !== service.id.toString()),
                                  serviceNames: prev.serviceNames.filter(name => name !== service.name)
                                }));
                              }
                            }}
                            className="w-4 h-4 text-pink-600 border-gray-300 rounded focus:ring-pink-500"
                          />
                          <span className="text-sm">{service.name} - ₺{service.price}</span>
                        </label>
                      );
                    })
                  )}
                </div>
                {formData.serviceIds.length > 0 && (
                  <div className="mt-1 text-xs text-gray-400">
                    Seçilen: {formData.serviceIds.length} hizmet
                  </div>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tarih & Saat</label>
                  <button
                    type="button"
                    onClick={openDateTimePicker}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 text-left bg-white hover:bg-gray-50 transition-colors"
                  >
                    {formData.date && formData.time ? (
                      <span className="text-gray-900">
                        {new Date(formData.date).toLocaleDateString('tr-TR')} - {formData.time}
                      </span>
                    ) : (
                      <span className="text-gray-500">Tarih ve saat seçin</span>
                    )}
                  </button>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fiyat</label>
                  <input
                    type="number"
                    value={formData.totalPrice}
                    onChange={(e) => setFormData({...formData, totalPrice: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Özel Süre (dk)</label>
                  <input
                    type="number"
                    value={formData.customDuration}
                    onChange={(e) => setFormData({...formData, customDuration: e.target.value})}
                    placeholder="Sadece bu randevu için"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
                  />
                  <div className="text-xs text-gray-500 mt-1">
                    Sadece bu randevu için geçerli, hizmet süresini değiştirmez
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Durum</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value as Appointment['status']})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
                  >
                    <option value="pending">Bekliyor</option>
                    <option value="confirmed">Onaylandı</option>
                    <option value="completed">Tamamlandı</option>
                    <option value="cancelled">İptal</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notlar</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
                  rows={3}
                />
              </div>
              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-pink-600 text-white py-2 px-4 rounded-md hover:bg-pink-700 transition-colors"
                >
                  Güncelle
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingAppointment(null);
                    resetForm();
                  }}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 transition-colors"
                >
                  İptal
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* DateTime Picker Modal */}
      {showDateTimePicker && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto text-white" style={{ backgroundColor: homeSettings?.secondaryBackgroundColor || '#1f2937' }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Tarih ve Saat Seçin</h3>
              <button
                onClick={() => setShowDateTimePicker(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Tarih Seçimi */}
              <div>
                <h4 className="font-medium text-gray-300 mb-3">Tarih Seçin</h4>
                <input
                  type="date"
                  value={selectedDateForTimeSlots}
                  onChange={(e) => handleDateSelect(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 text-white"
                  style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>

              {/* Saat Seçimi */}
              <div>
                <h4 className="font-medium text-gray-300 mb-3">
                  Saat Seçin {selectedDateForTimeSlots && `(${new Date(selectedDateForTimeSlots).toLocaleDateString('tr-TR')})`}
                </h4>
                
                {selectedDateForTimeSlots && formData.teamMemberId ? (
                  <div className="grid grid-cols-4 gap-2 max-h-60 overflow-y-auto">
                    {availableTimeSlots.map((slot) => (
                      <button
                        key={slot.time}
                        type="button"
                        onClick={() => handleTimeSelect(slot.time)}
                        className={`p-2 rounded-md text-xs font-medium transition-all ${
                          slot.available
                            ? 'bg-green-100 border border-green-300 text-green-800 hover:bg-green-200 cursor-pointer'
                            : 'bg-red-100 border border-red-300 text-red-600 hover:bg-red-200 cursor-pointer'
                        }`}
                        title={slot.conflictInfo ? `${slot.conflictInfo.customerName} - ${slot.conflictInfo.service}` : ''}
                      >
                        <div className="text-center">
                          <div className="font-semibold">{slot.time}</div>
                          {!slot.available && slot.conflictInfo && (
                            <div className="text-xs mt-1">
                              {slot.conflictInfo.customerName}
                            </div>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="text-gray-400 text-center py-8">
                    {!selectedDateForTimeSlots ? 'Önce tarih seçin' : 'Önce çalışan seçin'}
                  </div>
                )}
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowDateTimePicker(false)}
                className="px-4 py-2 border text-gray-300 rounded transition-colors"
                style={{ borderColor: homeSettings?.secondaryBackgroundColor || '#374151', backgroundColor: homeSettings?.backgroundColor || '#000000' }}
              >
                İptal
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notes Modal */}
      {showNotesModal && selectedAppointmentNotes && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto text-white" style={{ backgroundColor: homeSettings?.secondaryBackgroundColor || '#1f2937' }}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <span className="text-2xl mr-2">📝</span>
                <h3 className="text-lg font-semibold text-white">Randevu Notu</h3>
              </div>
              <button
                onClick={() => setShowNotesModal(false)}
                className="text-gray-400 hover:text-white text-2xl font-bold"
              >
                ×
              </button>
            </div>
            
            <div className="mb-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-4">
                <div className="grid gap-2 text-sm">
                  <div className="flex justify-between">
                    <span className="font-medium text-yellow-800">Müşteri:</span>
                    <span className="text-yellow-700">{selectedAppointmentNotes.customerName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-yellow-800">Tarih:</span>
                    <span className="text-yellow-700">{selectedAppointmentNotes.date}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-yellow-800">Saat:</span>
                    <span className="text-yellow-700">{selectedAppointmentNotes.time}</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-white border border-gray-200 rounded-md p-4">
                <div className="font-medium text-gray-900 mb-2">Not:</div>
                <div className="text-gray-700 whitespace-pre-wrap">
                  {selectedAppointmentNotes.notes}
                </div>
              </div>
            </div>
            
            <div className="flex justify-end">
              <button
                onClick={() => setShowNotesModal(false)}
                className="bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 transition-colors"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Conflict Confirmation Modal */}
      {showConflictModal && conflictData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto text-white" style={{ backgroundColor: homeSettings?.secondaryBackgroundColor || '#1f2937' }}>
            <div className="flex items-center mb-4">
              <AlertCircle className="w-6 h-6 text-red-500 mr-2" />
              <h3 className="text-lg font-semibold text-gray-900">
                {pendingAppointmentData?.id ? 'Randevu Onaylama Çakışması' : 'Randevu Çakışması Tespit Edildi'}
              </h3>
            </div>
            
            <div className="mb-4">
              <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
                <p className="text-red-800 font-medium">
                  {pendingAppointmentData?.id 
                    ? 'Aynı saatte aynı çalışanın onaylı bir randevusu var. Yine de onaylamak istiyor musunuz?'
                    : conflictData.message || 'Seçilen tarih ve saatte çakışan randevular bulunmaktadır:'
                  }
                </p>
              </div>
              
              {!pendingAppointmentData?.id && conflictData.conflicts && (
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                  <div className="grid gap-3">
                    {conflictData.conflicts.map((conflict: any, index: number) => (
                      <div key={index} className="bg-white border border-red-200 rounded-md p-3">
                        <div className="flex justify-between items-start mb-2">
                          <div className="font-medium text-gray-900">
                            {conflict.customerName || `Randevu #${conflict.appointmentId}`}
                          </div>
                          <div className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">
                            {Math.round(conflict.overlapMinutes)}dk çakışma
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                          <div>
                            <span className="font-medium">Başlangıç:</span> {conflict.startTime}
                          </div>
                          <div>
                            <span className="font-medium">Bitiş:</span> {conflict.endTime}
                          </div>
                          <div>
                            <span className="font-medium">Süre:</span> {conflict.duration} dakika
                          </div>
                          <div>
                            <span className="font-medium">Hizmet:</span> {conflict.service}
                          </div>
                        </div>
                        
                        <div className="mt-2 text-xs text-red-600">
                          ⚠️ Bu randevu ile {Math.round(conflict.overlapMinutes)} dakika çakışma var
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <div className="flex items-start">
                  <div className="text-blue-500 mr-2 mt-0.5">💡</div>
                  <div className="text-sm text-blue-800">
                    <div className="font-medium mb-1">Öneriler:</div>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Farklı bir saat seçin</li>
                      <li>Farklı bir ekip üyesi seçin</li>
                      <li>Çakışan randevuları iptal edin</li>
                      <li>Yine de devam etmek istiyorsanız "Devam Et" butonuna tıklayın</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={handleConflictConfirm}
                className="flex-1 bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 transition-colors"
              >
                {pendingAppointmentData?.id ? 'Yine de Onayla' : 'Yine de Devam Et'}
              </button>
              <button
                onClick={handleConflictCancel}
                className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 transition-colors"
              >
                İptal Et
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppointmentManagement;